import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { Store, select } from "@ngrx/store";

import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";

import { catchError, finalize, map, tap } from "rxjs/operators";
import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";
import { CurrentAccountMovementExtendedDTO } from "src/app/models/current-accounts/CurrentAccountDTO";

import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  DefaultPage,
  ExportEvent,
  FieldType,
  FilterEvent,
  FilterName,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { CurrentAccountsService } from "src/app/shared/services/current-accounts/current-accounts.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { subMenusIterator } from "src/app/shared/utils/menuUtils";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { setAdvancedFilter } from "src/app/shared/utils/tableUtils";
import { setSnackbar } from "src/app/store/actions/messages/message.actions";
import { AppState, loadedMenusState } from "src/app/store/reducers";
import { CHIP_STYLE, getChipStyle } from "src/app/shared/utils/customTableUtils";

const USAGE_CODE = "CURRENT_ACCOUNT_MOVEMENTS";

@Component({
  selector: "current-account-movements-list",
  templateUrl: "./current-account-movements.component.html",
  styleUrls: ["./current-account-movements.component.scss"],
})
export class CurrentAccountMovements implements OnInit, OnDestroy {
  @Input() set token(token: string) {
    if (token) {
      this._token = token;
    }
  }

  initialSort: SortEvent = {
    active: "movementOn",
    direction: "DESC",
  };

  private _token: string;

  private menus$: Observable<MenuDTO[]>;
  private menuSub: Subscription;

  public columnInfo$: Observable<ColumnSorterInfo[]>;
  public excelCurrentAccountMovements$: Observable<CurrentAccountMovementExtendedDTO[]>;
  public totalCurrentAccountMovements$ = new BehaviorSubject<number>(0);
  private getCurrentAccountMovementsSub: Subscription;

  public isLoading$ = new BehaviorSubject<boolean>(false);

  public locale: string = localStorage.getItem("locale");

  public tableTitle: string;
  public headerIcon: string;

  public tableData: CurrentAccountMovementExtendedDTO[];

  public tableConfig: TableConfig[] = [
    {
      header: "CurrentAccounts.Movements.movementOn",
      field: "movementOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      dateFilter: true,
    },
    {
      header: "CurrentAccounts.documentData",
      field: "documentData.doc_identifier",
      fieldType: FieldType.STRING,
    },
    {
      header: "CurrentAccounts.Movements.token",
      field: "token",
      fieldType: FieldType.STRING,
      identifier: true,
      hidden: true,
    },
    {
      header: "CurrentAccounts.Movements.amountBefore",
      field: "amountBefore",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
    },
    {
      header: "CurrentAccounts.Movements.amount",
      field: "amount",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
    },
    {
      header: "CurrentAccounts.Movements.amountAfter",
      field: "amountAfter",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
      style: "font-weight: 600;",
    },
    {
      header: "CurrentAccounts.Movements.currentAccountMovementType.token",
      field: "currentAccountMovementType.token",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "CurrentAccounts.Movements.currentAccountMovementType.code",
      field: "currentAccountMovementType.code",
      searchable: true,
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "CurrentAccounts.Movements.currentAccountMovementType.detailedDescription",
      field: "currentAccountMovementType.detailedDescription",
      searchable: true,
      fieldType: FieldType.CHIP_STRING,
      objectDescription: true,
    },
    {
      header: "CurrentAccounts.Movements.currentAccountToken",
      field: "currentAccountToken",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "CurrentAccounts.Movements.observation",
      field: "observation",
      fieldType: FieldType.STRING,
    },
    {
      header: "CurrentAccounts.Movements.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "CurrentAccounts.Movements.partyToken",
      field: "partyToken",
      fieldType: FieldType.STRING,
      hidden: true,
    },
  ];

  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private currentAccountsService: CurrentAccountsService,
    private translate: TranslateService,
    private titleService: Title,
    private tableConfigService: TableConfigService
  ) {}

  ngOnInit(): void {
    this.menus$ = this.store.pipe(select(loadedMenusState));
    this.loadScreenTitle();
    this.getTableConfig();
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    });
  }

  getCurrentAccountMovements(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.isLoading$.next(true);
    this.getCurrentAccountMovementsSub = this.currentAccountsService
      .getCurrentAccountMovements(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        advancedFilter
      )
      .pipe(
        map((result) => {
          const { items, size } = result;
          this.totalCurrentAccountMovements$.next(size);
          return this.setTableCustomElements(items);
        }),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("CurrentAccounts.Movements.currentAccountMovementsLoadingError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((result: CurrentAccountMovementExtendedDTO[]) => (this.tableData = result));
  }

  setTableCustomElements(items: any[]) {
    let customElements = items.map((item) => {
      let styleSettings: { style: string, icon: string };
      switch (item.currentAccountMovementType.code) {
        case 'DEBIT':
          styleSettings = getChipStyle(CHIP_STYLE.DANGER);
          styleSettings.style = "background-color: rgb(255, 175, 175); color: #890000;",
          styleSettings.icon = 'money_off';
          break;
        case 'CREDIT':
          styleSettings = getChipStyle(CHIP_STYLE.SUCCESS);
          styleSettings.icon = 'attach_money';
          break;
        default:
          styleSettings = getChipStyle(CHIP_STYLE.INACTIVE);
          break;
      }
      return {
        ...item, 
        currentAccountMovementType: { 
          ...item.currentAccountMovementType,
          detailedDescription: {
            value: item.currentAccountMovementType.detailedDescription[this.locale],
            icon: styleSettings.icon,
            style: styleSettings.style,
          }
        }
      }
    })
    return customElements;
  }

  setAdvancedFilter(filterEvent: FilterEvent) {
    const customerCardsFilter = {
      column: "currentAccountToken",
      operator: FilterName.EQUALS_TO,
      value: this._token,
    };

    return setAdvancedFilter(customerCardsFilter, filterEvent);
  }

  getCurrentAccountMovementsExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.isLoading$.next(true);
    this.excelCurrentAccountMovements$ = this.currentAccountsService
      .getCurrentAccountMovements(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
        },
        searchEvent,
        sortEvent,
        advancedFilter
      )
      .pipe(
        map(({ items }) => items),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("CurrentAccounts.currentAccountsLoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  getTableConfig() {
    this.columnInfo$ = this.tableConfigService.getConfigSettings(USAGE_CODE, ConfigSettingType.TABLE_CONFIG).pipe(
      map((response) => <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo),
      catchError((error) => {
        this.store.dispatch(
          setSnackbar(SnackBarUtils.setSnackBar(this.translate.instant("TableConfig.LoadError"), SnackbarType.ERROR))
        );
        return EMPTY;
      }),
      finalize(() => this.isLoading$.next(false))
    );
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getCurrentAccountMovements(pageEvent, sortEvent ?? this.initialSort, searchEvent, filterEvent);
  }

  onGetExportData(exportEvent: ExportEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    this.getCurrentAccountMovementsExcelList(pageEvent, sortEvent ?? this.initialSort, searchEvent, filterEvent);
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.isLoading$.next(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(this.translate.instant("TableConfig.SaveSuccess"), SnackbarType.SUCCESS)
            )
          );
        }),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(SnackBarUtils.setSnackBar(this.translate.instant("TableConfig.SaveError"), SnackbarType.ERROR))
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
    if (this.getCurrentAccountMovementsSub) {
      this.getCurrentAccountMovementsSub.unsubscribe();
    }
  }
}
